<template>
  <div id="portfolio-performance">
    <v-card class="px-4 pt-2 pb-4 my-0 elevation-0">
      <v-card-title class="px-1">
        <h1 class="card-title mb-4">
          <span v-if="type === 'yield'">
            {{ $t(`views.dashboardDetail.summary.portfolioPerformance.title.${type}.${calc}`) }}
          </span>
          <span v-else>
            {{ $t(`views.dashboardDetail.summary.portfolioPerformance.title.${type}`) }}
          </span>
        </h1>
        <div class="portfolio-performance-info-icon mr-5 mb-2">
          <a href="https://www.filios.app/blog/rentabilidad-inversiones/" target="_blank">
            <v-tooltip top :max-width="`300px`">
              <template v-slot:activator="{ on, attrs }">
                <v-icon :size="15" v-bind="attrs" color="softTextGrey lighten-2" v-on="on">
                  mdi-help-circle
                </v-icon>
              </template>
              <span>
                {{
                  type === 'yield'
                    ? $t(`views.dashboardDetail.summary.portfolioPerformance.info.${type}.${calc}`)
                    : $t(`views.dashboardDetail.summary.portfolioPerformance.info.${type}`)
                }}
              </span>
            </v-tooltip>
          </a>
        </div>
        <v-select
          v-if="user.role === 'admin' && type === 'yield' && calc === 'twr'"
          :value="benchmark"
          :items="benchmarkOptions"
          :label="$t(`views.dashboardDetail.summary.portfolioPerformance.benchmark.title`)"
          dense
          hide-details
          outlined
          class="mt-0 mb-4 reference-index-select"
          @change="benchmarkChanged"
        />

        <v-spacer />

        <div class="mb-2">
          <template v-if="type !== 'yield' || calc !== 'mwr'">
            <v-btn-toggle v-model="byYear" mandatory class="mb-2 mr-4 performance-button-group">
              <v-btn
                small
                color="softBackgroundGrey"
                :class="{ 'black--text': !$vuetify.theme.dark, 'white--text': $vuetify.theme.dark }"
                :value="true"
                @click="byYearChanged(true)"
              >
                {{ $t(`views.dashboardDetail.summary.portfolioPerformance.byYear.true`) }}
              </v-btn>
              <v-btn
                small
                color="softBackgroundGrey"
                :class="{ 'black--text': !$vuetify.theme.dark, 'white--text': $vuetify.theme.dark }"
                :value="false"
                @click="byYearChanged(false)"
              >
                {{ $t(`views.dashboardDetail.summary.portfolioPerformance.byYear.false`) }}
              </v-btn>
            </v-btn-toggle>
          </template>

          <template v-if="type === 'pl'">
            <v-btn-toggle v-model="plMode" mandatory class="mb-2 mr-4 performance-button-group">
              <v-btn
                small
                color="softBackgroundGrey"
                :class="{ 'black--text': !$vuetify.theme.dark, 'white--text': $vuetify.theme.dark }"
                value="percent"
              >
                {{ $t(`views.dashboardDetail.summary.portfolioPerformance.plMode.percent`) }} %
              </v-btn>
              <v-btn
                small
                color="softBackgroundGrey"
                :class="{ 'black--text': !$vuetify.theme.dark, 'white--text': $vuetify.theme.dark }"
                value="currency"
              >
                {{ $t(`views.dashboardDetail.summary.portfolioPerformance.plMode.currency`)
                }}{{ symbolBaseCurrency }}
              </v-btn>
              <v-btn
                small
                color="softBackgroundGrey"
                :class="{ 'black--text': !$vuetify.theme.dark, 'white--text': $vuetify.theme.dark }"
                value="net"
              >
                {{ $t(`views.dashboardDetail.summary.portfolioPerformance.plMode.net`) }}
              </v-btn>
            </v-btn-toggle>
          </template>

          <template v-if="type === 'yield'">
            <v-btn-toggle
              v-model="accumulated"
              mandatory
              class="mb-2 mr-4 performance-button-group"
            >
              <v-btn
                small
                color="softBackgroundGrey"
                :class="{ 'black--text': !$vuetify.theme.dark, 'white--text': $vuetify.theme.dark }"
                :value="true"
              >
                {{
                  $t(
                    `views.dashboardDetail.summary.portfolioPerformance.yield.accumulated.accumulated`
                  )
                }}
              </v-btn>
              <v-btn
                small
                color="softBackgroundGrey"
                :class="{ 'black--text': !$vuetify.theme.dark, 'white--text': $vuetify.theme.dark }"
                :value="false"
              >
                {{
                  $t(`views.dashboardDetail.summary.portfolioPerformance.yield.accumulated.normal`)
                }}
              </v-btn>
            </v-btn-toggle>
          </template>

          <template v-if="type === 'yield'">
            <v-btn-toggle v-model="plus" mandatory class="mb-2 mr-4 performance-button-group">
              <v-btn
                small
                color="softBackgroundGrey"
                :class="{ 'black--text': !$vuetify.theme.dark, 'white--text': $vuetify.theme.dark }"
                :value="false"
              >
                {{ $t(`views.dashboardDetail.summary.portfolioPerformance.yield.plus.false`) }}
              </v-btn>
              <v-btn
                small
                color="softBackgroundGrey"
                :class="{ 'black--text': !$vuetify.theme.dark, 'white--text': $vuetify.theme.dark }"
                :value="true"
              >
                {{ $t(`views.dashboardDetail.summary.portfolioPerformance.yield.plus.true`) }}
              </v-btn>
            </v-btn-toggle>
          </template>

          <v-btn-toggle v-model="dateRange" mandatory class="mb-2">
            <template v-if="type !== 'yield' || calc !== 'mwr'">
              <v-btn
                small
                color="softBackgroundGrey"
                :class="{ 'black--text': !$vuetify.theme.dark, 'white--text': $vuetify.theme.dark }"
                value="ytd"
                :disabled="byYear"
                @click="dateRangeChanged('ytd')"
              >
                {{ $t(`views.dashboardDetail.summary.portfolioPerformance.dateRange.ytd`) }}
              </v-btn>
              <v-btn
                small
                color="softBackgroundGrey"
                :class="{ 'black--text': !$vuetify.theme.dark, 'white--text': $vuetify.theme.dark }"
                value="1y"
                :disabled="byYear"
                @click="dateRangeChanged('1y')"
              >
                {{ $t(`views.dashboardDetail.summary.portfolioPerformance.dateRange.1y`) }}
              </v-btn>
              <v-btn
                small
                color="softBackgroundGrey"
                :class="{ 'black--text': !$vuetify.theme.dark, 'white--text': $vuetify.theme.dark }"
                value="3y"
                :disabled="byYear"
                @click="dateRangeChanged('3y')"
              >
                {{ $t(`views.dashboardDetail.summary.portfolioPerformance.dateRange.3y`) }}
              </v-btn>
            </template>
            <v-btn
              small
              color="softBackgroundGrey"
              :class="{ 'black--text': !$vuetify.theme.dark, 'white--text': $vuetify.theme.dark }"
              value="5y"
              @click="dateRangeChanged('5y')"
            >
              {{ $t(`views.dashboardDetail.summary.portfolioPerformance.dateRange.5y`) }}
            </v-btn>
            <v-btn
              small
              color="softBackgroundGrey"
              :class="{ 'black--text': !$vuetify.theme.dark, 'white--text': $vuetify.theme.dark }"
              value="max"
              @click="dateRangeChanged('max')"
            >
              {{ $t(`views.dashboardDetail.summary.portfolioPerformance.dateRange.max`) }}
            </v-btn>
          </v-btn-toggle>

          <ExportButton
            :click="downloadCsv"
            :loading="exportLoading"
            :disabled="!summary || summary.length === 0"
          />
        </div>
      </v-card-title>
      <v-card-text class="mt-5 pa-0">
        <BarChart
          v-if="loadedData && showBarChart"
          ref="barChart"
          :chartData="chartData"
          :chartOptions="chartOptions"
        />
        <LineChart
          v-else-if="loadedData"
          ref="lineChart"
          :chartData="chartData"
          :chartOptions="chartOptions"
        />
        <BarLoading v-else />

        <div class="mt-5">
          <SummaryPortfolioPerformanceTable
            ref="summaryPortfolioPerformanceTable"
            :summary="summary"
            :type="type"
            :byYear="byYear"
            :plMode="plMode"
            :accumulated="accumulated"
            :plus="plus"
            :loadedData="loadedData"
          />
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import FileSaver from 'file-saver'
import { map } from 'lodash'
import { mapState } from 'vuex'
import { getPositionPerformance, getPositionPerformanceCsv } from '@/lib/api.js'
import { DEFAULT_PALETTE, DARK_DEFAULT_PALETTE } from '@/lib/colors.js'
import { getCurrencyValueAsString, getCurrencySymbol } from '@/lib/utils/currencies.js'
import BarChart from '@/views/components/core/chartjs/BarChart'
import LineChart from '@/views/components/core/chartjs/LineChart'
import BarLoading from '@/views/components/core/BarLoading.vue'
import SummaryPortfolioPerformanceTable from '@/views/components/dashboardDetail/summary/SummaryPortfolioPerformanceTable'
import ExportButton from '@/views/components/common/ExportButton'

const BENCHMARK_MARKET_INDEXES = ['GSPC', 'NDX', 'STOXX50E', 'IBEX']

export default {
  components: { BarChart, LineChart, BarLoading, SummaryPortfolioPerformanceTable, ExportButton },
  props: {
    type: {
      type: String,
      required: true,
    },
    calc: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    dateRange: null, // filled in created
    byYear: false, // filled in created
    accumulated: false,
    plus: true,
    plMode: 'net',
    exportLoading: false,
    positionType: null,
    filters: [],
    loadedData: null,
    summary: null,
    chartData: {
      labels: null,
      datasets: null,
    },
    chartOptions: {},
    benchmark: null,
  }),
  computed: {
    ...mapState(['user', 'hideAmounts']),
    symbolBaseCurrency() {
      return getCurrencySymbol(this.user.baseCurrency)
    },
    showBarChart() {
      return this.type === 'yield' && !this.accumulated
    },
    benchmarkOptions() {
      const benchmarkOptions = [
        {
          text: this.$t(
            'views.dashboardDetail.summary.portfolioPerformance.benchmark.options.none'
          ),
          value: null,
        },
      ]
      BENCHMARK_MARKET_INDEXES.forEach((index) => {
        benchmarkOptions.push({
          text: this.$t(
            `views.dashboardDetail.summary.portfolioPerformance.benchmark.options.${index}`
          ),
          value: index,
        })
      })
      return benchmarkOptions
    },
  },
  watch: {
    '$vuetify.theme.dark'() {
      this.createLineChartObject()
    },
    plMode() {
      this.createLineChartObject()
    },
    accumulated() {
      this.createLineChartObject()
    },
    plus() {
      this.createLineChartObject()
    },
  },
  created() {
    if (this.type === 'yield' && this.calc === 'mwr') {
      this.dateRange = '5y'
      this.byYear = true
    } else {
      this.dateRange = '1y'
    }
  },
  methods: {
    dateRangeChanged(dateRange) {
      this.dateRange = dateRange
      this.loadSummary()
    },
    benchmarkChanged(benchmark) {
      this.benchmark = benchmark
      this.loadSummary()
    },
    byYearChanged(byYear) {
      this.byYear = byYear
      if (this.byYear) {
        this.dateRange = '5y'
      } else {
        this.dateRange = '1y'
      }
      this.loadSummary()
    },
    async load({ filters, positionType }) {
      this.filters = filters
      this.positionType = positionType
      await this.loadSummary()
    },
    async loadSummary() {
      this.loadedData = false
      try {
        const positionType =
          this.positionType === 'all'
            ? undefined
            : this.positionType === 'allNeCash'
            ? { $ne: 'cash' }
            : this.positionType
        const summary = await getPositionPerformance({
          type: this.type,
          queryString: {
            dateRange: this.dateRange,
            calc: this.type === 'yield' ? this.calc : undefined,
            byYear: this.byYear,
            filter: {
              type: positionType,
              ...this.filters,
            },
            compareWithBenchmark: this.benchmark,
          },
        })
        this.summary = summary.data.performance
        this.benchmarkSummary = summary.data.benchmarkPerformance
        this.createLineChartObject()
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }
      this.loadedData = true
    },
    async downloadCsv() {
      this.exportLoading = true

      const nowFormatted = DateTime.now().toFormat('yyyy_MM_dd HH_mm_ss')
      try {
        const positionType =
          this.positionType === 'all'
            ? undefined
            : this.positionType === 'allNeCash'
            ? { $ne: 'cash' }
            : this.positionType
        const result = await getPositionPerformanceCsv({
          type: this.type,
          queryString: {
            dateRange: this.dateRange,
            calc: this.type === 'yield' ? this.calc : undefined,
            byYear: this.byYear,
            filter: {
              type: positionType,
              ...this.filters,
            },
          },
        })
        const blob = new Blob([result.data], { type: 'text/csv;charset=utf-8' })
        const calcTitle =
          this.type === 'yield'
            ? (this.calc || '').toUpperCase()
            : this.$t(`views.dashboardDetail.summary.portfolioPerformance.title.${this.type}`)
        FileSaver.saveAs(blob, `Filios - ${calcTitle} - ${nowFormatted}.csv`)
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }

      this.exportLoading = false
    },
    chartScalesYTicksCallBack(value) {
      if (this.type === 'pl') {
        if (this.plMode === 'percent') {
          return (
            getCurrencyValueAsString({
              value,
            }) + '%'
          )
        } else {
          return getCurrencyValueAsString({
            value,
            currency: this.user.baseCurrency,
            hide: this.hideAmounts,
          })
        }
      } else if (this.type === 'yield') {
        return (
          getCurrencyValueAsString({
            value,
          }) + '%'
        )
      }
    },
    chartTooltipLabelCallback(tooltipItem, data) {
      let label = data.datasets[tooltipItem.datasetIndex].label || ''
      if (BENCHMARK_MARKET_INDEXES.indexOf(data.datasets[tooltipItem.datasetIndex].label) > -1) {
        label = this.$t(
          `views.dashboardDetail.summary.portfolioPerformance.benchmark.options.${
            data.datasets[tooltipItem.datasetIndex].label
          }`
        )
      }

      if (label) label += ': '
      if (this.type === 'pl') {
        if (this.plMode === 'percent') {
          label += getCurrencyValueAsString({
            value: tooltipItem.yLabel,
          })
          label += '%'
        } else {
          label += getCurrencyValueAsString({
            value: tooltipItem.yLabel,
            currency: this.user.baseCurrency,
            hide: this.hideAmounts,
          })
        }
      } else if (this.type === 'yield') {
        label += getCurrencyValueAsString({
          value: tooltipItem.yLabel,
        })
        label += '%'
      }

      return label
    },
    getChartOptions() {
      return {
        maintainAspectRatio: false,
        legend: {
          display: this.type === 'pl',
          position: this.type === 'pl' ? 'top' : undefined,
        },
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false,
              },
            },
          ],
          yAxes: [
            {
              ticks: {
                callback: this.chartScalesYTicksCallBack,
              },
            },
          ],
        },
        tooltips: {
          intersect: false,
          callbacks: {
            label: this.chartTooltipLabelCallback,
          },
        },
      }
    },
    createLineChartObject() {
      if (!this.summary) return
      const data = JSON.parse(JSON.stringify(this.summary))
      const benchmarkData =
        this.benchmarkSummary && this.benchmarkSummary.length > 0
          ? JSON.parse(JSON.stringify(this.benchmarkSummary))
          : null

      if (this.type === 'pl') {
        if (this.plMode === 'net') {
          this.chartData.datasets = [
            {
              label: this.$t('views.dashboardDetail.summary.portfolioPerformance.totalValuePlus'),
              data: data.map((elem) => elem.totalValueBaseCurrencyPlus),
              borderColor: DEFAULT_PALETTE.softGreen,
              pointBackgroundColor: DEFAULT_PALETTE.softGreen,
            },
            {
              label: this.$t('views.dashboardDetail.summary.portfolioPerformance.totalValue'),
              data: data.map((elem) => elem.totalValueBaseCurrency),
              borderColor: DEFAULT_PALETTE.primary,
              pointBackgroundColor: DEFAULT_PALETTE.primary,
            },
            {
              label: this.$t('views.dashboardDetail.summary.portfolioPerformance.totalCost'),
              data: data.map((elem) => elem.totalCostWithComissionBaseCurrency),
              borderColor: this.$vuetify.theme.dark
                ? DARK_DEFAULT_PALETTE.dark_Textgrey
                : DEFAULT_PALETTE.secondary,
              pointBackgroundColor: this.$vuetify.theme.dark
                ? DARK_DEFAULT_PALETTE.dark_Textgrey
                : DEFAULT_PALETTE.secondary,
            },
          ]
        } else if (this.plMode === 'percent') {
          this.chartData.datasets = [
            {
              label: this.$t(
                'views.dashboardDetail.summary.portfolioPerformance.plPlusBaseCurrencyPercent'
              ),
              data: data.map((elem) => elem.plPlusBaseCurrencyPercent),
              borderColor: DEFAULT_PALETTE.softGreen,
              pointBackgroundColor: DEFAULT_PALETTE.softGreen,
            },
            {
              label: this.$t(
                'views.dashboardDetail.summary.portfolioPerformance.plBaseCurrencyPercent'
              ),
              data: data.map((elem) => elem.plBaseCurrencyPercent),
              borderColor: DEFAULT_PALETTE.primary,
              pointBackgroundColor: DEFAULT_PALETTE.primary,
            },
          ]
        } else if (this.plMode === 'currency') {
          this.chartData.datasets = [
            {
              label:
                this.$t('views.dashboardDetail.summary.portfolioPerformance.plPlusBaseCurrency') +
                `(${this.symbolBaseCurrency})`,
              data: data.map((elem) => elem.plPlusBaseCurrency),
              borderColor: DEFAULT_PALETTE.softGreen,
              pointBackgroundColor: DEFAULT_PALETTE.softGreen,
            },
            {
              label:
                this.$t('views.dashboardDetail.summary.portfolioPerformance.plBaseCurrency') +
                `(${this.symbolBaseCurrency})`,
              data: data.map((elem) => elem.plBaseCurrency),
              borderColor: DEFAULT_PALETTE.primary,
              pointBackgroundColor: DEFAULT_PALETTE.primary,
            },
          ]
        }
      } else if (this.type === 'yield') {
        let dataset, datasetBenchmark
        if (this.plus) {
          const barBackgroundColorPlus = data.map((elem) =>
            elem.yieldPlus < 0
              ? this.$vuetify.theme.dark
                ? DARK_DEFAULT_PALETTE.chartsBarRed
                : DEFAULT_PALETTE.chartsBarRed
              : this.$vuetify.theme.dark
              ? DARK_DEFAULT_PALETTE.chartsBarGreen
              : DEFAULT_PALETTE.chartsBarGreen
          )
          dataset = {
            label: this.accumulated
              ? this.$t(
                  'views.dashboardDetail.summary.portfolioPerformance.yield.yieldPlus.accumulated'
                )
              : this.$t(
                  'views.dashboardDetail.summary.portfolioPerformance.yield.yieldPlus.normal'
                ),
            data: data.map((elem) =>
              this.accumulated ? elem.accumulatedYieldPlus : elem.yieldPlus
            ),
            borderColor: this.accumulated ? DEFAULT_PALETTE.softGreen : undefined,
            pointBackgroundColor: this.accumulated ? DEFAULT_PALETTE.softGreen : undefined,
            backgroundColor: !this.accumulated ? barBackgroundColorPlus : undefined,
          }
        } else {
          const barBackgroundColor = data.map((elem) =>
            elem.yield < 0
              ? this.$vuetify.theme.dark
                ? DARK_DEFAULT_PALETTE.chartsBarRed
                : DEFAULT_PALETTE.chartsBarRed
              : this.$vuetify.theme.dark
              ? DARK_DEFAULT_PALETTE.chartsBarGreen
              : DEFAULT_PALETTE.chartsBarGreen
          )
          dataset = {
            label: this.accumulated
              ? this.$t(
                  'views.dashboardDetail.summary.portfolioPerformance.yield.yield.accumulated'
                )
              : this.$t('views.dashboardDetail.summary.portfolioPerformance.yield.yield.normal'),
            data: data.map((elem) => (this.accumulated ? elem.accumulatedYield : elem.yield)),
            borderColor: this.accumulated ? DEFAULT_PALETTE.softGreen : undefined,
            pointBackgroundColor: this.accumulated ? DEFAULT_PALETTE.softGreen : undefined,
            backgroundColor: !this.accumulated ? barBackgroundColor : undefined,
          }
        }

        if (benchmarkData) {
          datasetBenchmark = {
            label: this.benchmark,
            data: benchmarkData.map((elem) =>
              this.accumulated ? elem.accumulatedYield : elem.yield
            ),
            borderColor: this.accumulated
              ? this.$vuetify.theme.dark
                ? DARK_DEFAULT_PALETTE.dark_Textgrey
                : DARK_DEFAULT_PALETTE.chartsBarBlue
              : undefined,
            pointBackgroundColor: this.accumulated
              ? this.$vuetify.theme.dark
                ? DARK_DEFAULT_PALETTE.dark_Textgrey
                : DARK_DEFAULT_PALETTE.chartsBarBlue
              : undefined,
            backgroundColor: !this.accumulated
              ? this.$vuetify.theme.dark
                ? DARK_DEFAULT_PALETTE.dark_Textgrey
                : DEFAULT_PALETTE.chartsBarBlue
              : undefined,
          }
        }

        this.chartData.datasets = datasetBenchmark ? [dataset, datasetBenchmark] : [dataset]
      }

      this.chartData.labels = map(data, (item) =>
        this.byYear
          ? item.year
          : this.$t(`common.months.${item.month}`).substring(0, 3) +
            ` ${item.year.toString().slice(-2)}`
      )

      this.chartOptions = this.getChartOptions()

      if (this.showBarChart && this.$refs.barChart) this.$refs.barChart.render()
      else if (this.$refs.lineChart) this.$refs.lineChart.render()
    },
  },
}
</script>

<style lang="scss">
#portfolio-performance {
  .performance-button-group {
    .v-btn__content {
      text-transform: none;
    }
  }
  .portfolio-performance-info-icon {
    a {
      text-decoration: none;
      i {
        margin-bottom: 10px;
        margin-left: 5px;
      }
    }
  }
  .reference-index-select {
    max-width: 150px;
    font-size: 12px;
  }
  .v-select.reference-index-select .v-input__slot {
    min-height: 32px !important;
  }
  .v-select.reference-index-select .v-select__selections {
    padding: 0 !important;
  }
}
</style>
